import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, MessageSquare, ShieldCheck, MapPin, Users, Heart, Sparkles, ChevronDown, Star, Calendar, Award, Clock, Facebook, ThumbsUp } from 'lucide-react';
import { Seo, localBusinessJsonLd, faqJsonLd } from '@/components/Seo';
import { LeadForm } from '@/components/LeadForm';
import { ReviewsSlideshow } from '@/components/ReviewsSlideshow';
import { StudioSlideshow } from '@/components/StudioSlideshow';
import { BeforeAfterCompare } from '@/components/BeforeAfterCompare';
import { SITE, SERVICES, LOCATIONS, PHOTOS, STUDIO_GALLERY, TRANSFORMATIONS } from '@/data/site-config';

const HOME_FAQS = [
  { q: `Where is the studio located in Stuart, FL?`, a: `Our private personal training studio is at ${SITE.address.full} — on SE Dixie Highway in south ${SITE.city}. We serve clients across all of ${SITE.county}, FL by appointment.` },
  { q: 'Do you offer private personal training or group training?', a: `Both. We offer private 1-on-1 personal training and semi-private (small-group) training in the same private studio. Each person inside a semi-private session still has individual programming.` },
  { q: 'What are your hours?', a: `${SITE.hoursDetail} The studio is appointment-only, so we schedule each session in advance — there are no walk-ins.` },
  { q: 'Is Jarad certified?', a: `Yes. ${SITE.owner} is a NASM-Certified Personal Trainer and the owner of Fitness Training 360.` },
  { q: 'Do I need to be in shape to start?', a: `No. We coach people at every starting point and adjust the work to fit you. The goal is to make training feel doable, not intimidating.` },
  { q: 'How do I book a free consultation?', a: `Submit the form on this page, call ${SITE.phone}, or book directly on Vagaro. We’ll talk through your goals and confirm we’re a fit.` },
];

export default function HomePage() {
  return (
    <>
      <Seo
        title={`Personal Trainer in ${SITE.city}, ${SITE.stateAbbr} — Private Gym & 1-on-1 Coaching`}
        description={`Looking for a personal trainer in ${SITE.city}, ${SITE.stateAbbr}? Fitness Training 360 is a private gym in ${SITE.county} owned by NASM-certified coach ${SITE.owner}. Private 1-on-1, small-group, nutrition, golf fitness & online coaching. Book a free consultation.`}
        canonical="/"
        image={PHOTOS.jaradFrontDoor}
        jsonLd={[localBusinessJsonLd(), faqJsonLd(HOME_FAQS)]}
      />

      {/* HERO — image-dominant. Mobile: image top → text → CTAs. Desktop: text stacked left, big image right. */}
      <section className="hero-gradient" data-testid="home-hero">
        <div className="container-x pt-6 lg:pt-12 pb-12 lg:pb-16">
          <div className="grid gap-8 lg:gap-12 lg:grid-cols-12 items-center">
            {/* Image — on mobile this renders first (order-1), on desktop it sits to the right (lg:order-2). */}
            <div className="order-1 lg:order-2 lg:col-span-6">
              <div className="relative">
                <div className="hidden lg:block absolute -inset-4 rounded-3xl bg-gradient-to-br from-[rgba(200,255,0,0.18)] to-transparent blur-xl -z-0" aria-hidden="true" />
                <div className="relative surface overflow-hidden glow">
                  <img
                    src={PHOTOS.coachingClient}
                    alt={`${SITE.owner}, NASM-certified personal trainer and owner of Fitness Training 360, coaching at the private studio in ${SITE.city}, ${SITE.stateAbbr}`}
                    className="w-full h-[360px] sm:h-[460px] lg:h-[600px] object-cover"
                    loading="eager"
                    fetchPriority="high"
                  />
                  <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 bg-gradient-to-t from-[rgba(9,11,14,0.95)] via-[rgba(9,11,14,0.5)] to-transparent">
                    <div className="flex items-center gap-2 text-xs sm:text-sm">
                      <span className="badge badge-new">NASM-Certified</span>
                      <span className="text-[color:var(--ft-muted)]">{SITE.owner} · Owner & Lead Trainer</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Text stack — on mobile after image (order-2), on desktop on the left (lg:order-1). */}
            <div className="order-2 lg:order-1 lg:col-span-6">
              <span className="eyebrow"><Sparkles size={12} /> {SITE.city}, {SITE.stateAbbr} · {SITE.county}</span>
              <h1 className="font-display text-[36px] sm:text-[44px] lg:text-[58px] font-bold leading-[1.05] tracking-tight mt-4">
                Personal Trainer in <span className="whitespace-nowrap">{SITE.city}, {SITE.stateAbbr}</span>
                <span className="block text-accent mt-1">A private gym built around you.</span>
              </h1>
              <p className="mt-5 text-lg md:text-xl text-[color:var(--ft-muted)] max-w-2xl">
                {SITE.name} is a private personal training studio in {SITE.city}, owned by NASM-certified coach {SITE.owner}. Private 1-on-1, small-group, nutrition coaching, golf fitness, and online programming — all built around the person in front of us.
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <a href="#lead" className="btn btn-primary btn-lg" data-testid="hero-cta">Free consultation <ArrowRight size={18} /></a>
                <a href={SITE.booking.url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-lg" data-testid="hero-book"><Calendar size={18} /> Book on Vagaro</a>
                <a href={`tel:${SITE.phoneE164}`} className="btn btn-ghost btn-lg"><Phone size={18} /> {SITE.phone}</a>
              </div>

              <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
                {[
                  { icon: <Award size={16} />, label: 'NASM-Certified' },
                  { icon: <ShieldCheck size={16} />, label: 'Private studio' },
                  { icon: <Users size={16} />, label: '1-on-1 & small group' },
                  { icon: <Clock size={16} />, label: 'By appointment' },
                ].map((t, i) => (
                  <div key={i} className="surface px-3 py-3 flex items-center gap-2 text-sm text-[color:var(--ft-muted)]">
                    <span className="text-accent">{t.icon}</span>{t.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD FORM — dedicated section right under the hero */}
      <section id="lead" className="section-sm" data-testid="home-lead-section">
        <div className="container-x grid gap-8 lg:grid-cols-12 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow">Start here</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">Book your free consultation.</h2>
            <p className="mt-4 text-[color:var(--ft-muted)] text-lg">No pressure, no contract talk on day one. Tell us your goal and we’ll talk through whether the studio is the right fit.</p>
            <ul className="mt-5 grid gap-2 text-[color:var(--ft-muted)]">
              <li className="flex items-start gap-2"><span className="text-accent">✓</span> Coached personally by {SITE.owner} (NASM-CPT)</li>
              <li className="flex items-start gap-2"><span className="text-accent">✓</span> Programs built for your starting point</li>
              <li className="flex items-start gap-2"><span className="text-accent">✓</span> Private studio — your time is your time</li>
              <li className="flex items-start gap-2"><span className="text-accent">✓</span> {SITE.hoursLabel}</li>
            </ul>
            <div className="mt-6 surface p-4 flex items-center gap-3">
              <Phone size={16} className="text-accent" />
              <div>
                <div className="text-xs text-[color:var(--ft-dim)]">Prefer to talk?</div>
                <a href={`tel:${SITE.phoneE164}`} className="font-semibold hover:text-accent">{SITE.phone}</a>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <LeadForm source="/" testIdPrefix="hero-lead" />
          </div>
        </div>
      </section>

      {/* MISSION / POSITIONING */}
      <section className="section-sm">
        <div className="container-x">
          <div className="surface p-6 md:p-8 grid gap-6 md:grid-cols-12 items-center">
            <div className="md:col-span-7">
              <span className="eyebrow">Our mission</span>
              <p className="font-display text-2xl md:text-3xl mt-3 leading-snug">“{SITE.mission}”</p>
            </div>
            <div className="md:col-span-5 text-[color:var(--ft-muted)]">
              <p>That belief shapes how we coach: <strong className="text-[color:var(--ft-text)]">private gym environment</strong>, individual programming, and steady progress that fits a real life in {SITE.county}.</p>
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-3 mt-6">
            {[
              { title: 'Personal, not a class', body: 'Every session is built for one person at a time. Your program. Your pace. Your weaknesses get addressed — not skipped over.' },
              { title: 'Local owner. Local accountability.', body: `${SITE.owner} is a longtime ${SITE.county} local who owns and coaches at the studio. You’re not just another swipe-in at a chain gym.` },
              { title: 'A real community, not a crowd', body: 'A private, welcoming environment where everyone from beginners to longtime clients is treated like family.' },
            ].map((b, i) => (
              <div key={i} className="surface p-6">
                <h3 className="font-display text-xl font-semibold mb-2">{b.title}</h3>
                <p className="text-[color:var(--ft-muted)] leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-sm">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-7">
            <div>
              <span className="eyebrow">Services</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">Personal training coaching that fits your life.</h2>
              <p className="text-[color:var(--ft-muted)] mt-2 max-w-2xl">From 1-on-1 personal training to small-group, nutrition, and golf fitness — every service runs out of our private {SITE.city} gym.</p>
            </div>
            <Link to="/services" className="btn btn-ghost btn-sm">All services <ArrowRight size={14} /></Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="surface card-hover p-6 block" data-testid={`service-${s.slug}`}>
                <h3 className="font-display text-xl font-semibold mb-1">{s.title}</h3>
                <p className="text-[color:var(--ft-muted)] text-sm leading-relaxed">{s.short}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-accent">Learn more <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT REVIEWS (Google) — slideshow */}
      <ReviewsSlideshow />

      {/* REAL CLIENT TRANSFORMATIONS */}
      {TRANSFORMATIONS[0] && (
        <section className="section-sm" data-testid="home-transformations-section">
          <div className="container-x">
            <div className="grid gap-8 lg:grid-cols-12 items-center">
              {/* Left: story + metrics */}
              <div className="lg:col-span-5 order-2 lg:order-1">
                <span className="eyebrow">Real client results</span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">
                  {TRANSFORMATIONS[0].clientName.split(' ')[0]} lost {TRANSFORMATIONS[0].metrics[0].value} in {TRANSFORMATIONS[0].durationLabel}.
                </h2>
                <p className="mt-4 text-[color:var(--ft-muted)] text-lg leading-relaxed">
                  Real client. Real numbers. {TRANSFORMATIONS[0].coachSummary}
                </p>

                <div className="grid grid-cols-3 gap-3 mt-6">
                  {TRANSFORMATIONS[0].metrics.map((m, i) => (
                    <div key={m.label} className="surface p-4 text-center" data-testid={`home-transformation-metric-${i}`}>
                      <div className="font-display text-2xl font-bold text-accent">{m.value}</div>
                      <div className="text-xs text-[color:var(--ft-muted)] mt-1 leading-tight">{m.label}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/client-transformations" className="btn btn-primary" data-testid="home-transformation-cta">
                    See her full story <ArrowRight size={16} />
                  </Link>
                  <Link to="/contact" className="btn btn-secondary" data-testid="home-transformation-book-cta">
                    Start yours
                  </Link>
                </div>
                <p className="text-xs text-[color:var(--ft-dim)] mt-4">
                  Published with {TRANSFORMATIONS[0].clientName}&rsquo;s consent. Individual results vary.
                </p>
              </div>

              {/* Right: before/after slider */}
              <div className="lg:col-span-7 order-1 lg:order-2">
                <BeforeAfterCompare
                  before={TRANSFORMATIONS[0].beforeImage}
                  after={TRANSFORMATIONS[0].afterImage}
                  initial={50}
                  aspectClass="aspect-[4/5] sm:aspect-[3/4]"
                  testId="home-transformation-compare"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ABOUT PREVIEW */}
      <section className="section-sm">
        <div className="container-x grid gap-8 lg:grid-cols-12 items-center">
          <div className="lg:col-span-5">
            <div className="surface overflow-hidden">
              <img src={PHOTOS.jaradAtDesk} alt={`${SITE.owner}, NASM-certified personal trainer and owner of Fitness Training 360`} className="w-full h-72 sm:h-96 object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="eyebrow">Meet your coach</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">{SITE.owner} — NASM-CPT, owner & lead trainer.</h2>
            <p className="mt-4 text-[color:var(--ft-muted)] text-lg leading-relaxed">{SITE.owner} owns and operates Fitness Training 360 as a private personal training studio in {SITE.city}. He’s a NASM-Certified Personal Trainer and a longtime {SITE.county} local committed to building real, lasting coaching relationships — the kind where you’re actually known by your trainer.</p>
            <ul className="mt-5 grid sm:grid-cols-2 gap-3">
              {[
                'Coaches every client personally',
                'Programs built around your goals',
                'Focused on long-term habits',
                'Welcoming, judgment-free studio',
              ].map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-[color:var(--ft-muted)]"><span className="text-accent">•</span>{p}</li>
              ))}
            </ul>
            <Link to="/about-jarad-wingard" className="btn btn-secondary mt-6" data-testid="home-about-cta">Read Jarad’s story <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* CONNECT WITH JARAD ON FACEBOOK */}
      <section className="section-sm" data-testid="home-facebook-section">
        <div className="container-x">
          <div
            className="relative overflow-hidden rounded-2xl border border-[color:var(--ft-border)]"
            style={{ background: 'linear-gradient(135deg, rgba(24,119,242,0.10) 0%, rgba(24,119,242,0.02) 45%, rgba(9,11,14,1) 100%)' }}
          >
            <div
              className="absolute -top-32 -right-20 w-[440px] h-[440px] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(closest-side, rgba(24,119,242,0.30), rgba(24,119,242,0) 70%)' }}
              aria-hidden="true"
            />
            <div className="relative grid gap-8 md:grid-cols-12 items-center p-7 md:p-12">
              <div className="md:col-span-7">
                <span className="eyebrow" style={{ background: 'rgba(24,119,242,0.10)', color: '#5fb4ff', borderColor: 'rgba(24,119,242,0.30)' }}>
                  <Facebook size={12} fill="#5fb4ff" /> Facebook
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">Connect with {SITE.owner.split(' ')[0]} on Facebook.</h2>
                <p className="mt-4 text-[color:var(--ft-muted)] text-lg max-w-xl">Behind-the-scenes from the studio, training tips, and client wins. Send him a friend request — the best way to get to know your coach before you ever walk in.</p>
                <ul className="mt-5 grid gap-2 text-[color:var(--ft-muted)]">
                  <li className="flex items-start gap-2"><ThumbsUp size={16} className="mt-1 text-[#5fb4ff]" /> See real client wins as they happen</li>
                  <li className="flex items-start gap-2"><ThumbsUp size={16} className="mt-1 text-[#5fb4ff]" /> Get a feel for the FT360 community vibe</li>
                  <li className="flex items-start gap-2"><ThumbsUp size={16} className="mt-1 text-[#5fb4ff]" /> Message Jarad directly with any question</li>
                </ul>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a href={SITE.social.facebookOwner} target="_blank" rel="noreferrer" className="fb-chip" data-testid="home-fb-jarad-cta" style={{ padding: '14px 22px', fontSize: 16 }}>
                    <Facebook size={18} fill="#fff" /> Visit Jarad’s Facebook
                  </a>
                  <a href={SITE.social.facebook} target="_blank" rel="noreferrer" className="btn btn-secondary btn-lg" data-testid="home-fb-business-cta">
                    <Facebook size={18} /> Fitness Training 360 page
                  </a>
                </div>
              </div>
              <div className="md:col-span-5 flex justify-center md:justify-end">
                <a
                  href={SITE.social.facebookOwner}
                  target="_blank"
                  rel="noreferrer"
                  className="fb-glow"
                  aria-label="Visit Jarad Wingard on Facebook"
                  data-testid="home-fb-glow"
                  style={{ width: 168, height: 168 }}
                >
                  <Facebook size={84} fill="#fff" strokeWidth={0} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-sm">
        <div className="container-x">
          <span className="eyebrow">Getting started</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-7">How it works.</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              { n: '01', t: 'Reach out', b: 'Submit the form, call, text, or book on Vagaro. Tell us your goal in a sentence or two.' },
              { n: '02', t: 'Free consultation', b: 'We sit down (in person or by phone) to understand your starting point, schedule, and goals.' },
              { n: '03', t: 'Get started', b: 'We build your plan and book your sessions. Coaching, accountability, and adjustments along the way.' },
            ].map((s) => (
              <div key={s.n} className="surface p-6">
                <div className="text-accent font-display font-bold text-sm mb-2">STEP {s.n}</div>
                <h3 className="font-display text-xl font-semibold mb-1">{s.t}</h3>
                <p className="text-[color:var(--ft-muted)] leading-relaxed text-sm">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FACILITY PREVIEW — See our studio */}
      <section className="section-sm" data-testid="home-studio-section">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div className="max-w-2xl">
              <span className="eyebrow">See our studio</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">A private gym to actually focus.</h2>
              <p className="mt-4 text-[color:var(--ft-muted)] text-lg leading-relaxed">
                Forget crowded rooms and waiting on equipment. Our private studio in south {SITE.city} is yours for your session — quiet, focused, and built for serious coaching.
              </p>
            </div>
            <Link to="/facility" className="btn btn-secondary" data-testid="home-studio-cta">
              See the full facility <ArrowRight size={16} />
            </Link>
          </div>
          <StudioSlideshow images={STUDIO_GALLERY} testId="home-studio-slideshow" />
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="section-sm">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <div>
              <span className="eyebrow">Service area</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">Serving all of {SITE.county}.</h2>
            </div>
            <Link to="/locations" className="btn btn-ghost btn-sm">All areas <ArrowRight size={14} /></Link>
          </div>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
            {LOCATIONS.map((l) => (
              <Link key={l.slug} to={`/locations/${l.slug}`} className="surface card-hover p-4">
                <div className="flex items-center gap-2"><MapPin size={14} className="text-accent" /><span className="font-semibold">{l.name}</span></div>
                <div className="text-[color:var(--ft-dim)] text-xs mt-1">{l.drive} to studio</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-sm">
        <div className="container-x max-w-3xl">
          <span className="eyebrow">FAQ</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-6">Common questions about personal training in Stuart.</h2>
          <div className="grid gap-2">
            {HOME_FAQS.map((f, i) => (
              <details key={i} className="surface p-5 group">
                <summary className="flex items-center justify-between cursor-pointer list-none font-semibold">
                  {f.q}
                  <ChevronDown size={18} className="transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-[color:var(--ft-muted)] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section">
        <div className="container-x">
          <div className="surface p-8 md:p-12 grid gap-6 md:grid-cols-12 items-center">
            <div className="md:col-span-7">
              <span className="eyebrow"><Star size={12} /> Ready to start</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">Train with a NASM-certified coach in {SITE.city}.</h2>
              <p className="mt-3 text-[color:var(--ft-muted)] text-lg">Book your free consultation or schedule directly on Vagaro.</p>
            </div>
            <div className="md:col-span-5 flex flex-wrap gap-3 md:justify-end">
              <Link to="/contact" className="btn btn-primary btn-lg">Free consultation <ArrowRight size={18} /></Link>
              <a href={SITE.booking.url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-lg"><Calendar size={18} /> Vagaro</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
